import { useEffect, useRef } from "react";

import { useConversationMessages, useConversations } from "~/ai-chat/data-source/use-conversations";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import type { ChatMessage } from "~/ai-chat/model/chat-message";
import type { ConversationMessage } from "~/ai-chat/model/conversation";
import { ConversationList } from "~/ai-chat/ui/conversation-list";

function toTranscriptItem(message: ConversationMessage): ChatMessage {
  return { kind: "message", id: message.id, role: message.role, content: message.content };
}

/**
 * Chat-history sidebar: lists the user's conversations, loads a selected one
 * into the transcript, and starts a fresh chat. Loading is guarded so a
 * selected conversation is hydrated into the transcript once — later
 * invalidations (e.g. after persisting a turn) don't clobber the live view.
 */
export function AiChatHistorySidebar() {
  const { activeConversationId, setActiveConversationId, setChatTitle, replaceMessages, startNewConversation } =
    useAiChat();
  const conversationsQuery = useConversations();
  const messagesQuery = useConversationMessages(activeConversationId);
  const hydratedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (activeConversationId === null) {
      hydratedIdRef.current = null;
      return;
    }
    if (activeConversationId !== hydratedIdRef.current && messagesQuery.data !== undefined) {
      replaceMessages(messagesQuery.data.map(toTranscriptItem));
      hydratedIdRef.current = activeConversationId;
    }
  }, [activeConversationId, messagesQuery.data, replaceMessages]);

  const conversations = conversationsQuery.data ?? [];

  const select = (id: string) => {
    setActiveConversationId(id);
    setChatTitle(conversations.find((conversation) => conversation.id === id)?.title ?? null);
  };

  return (
    <ConversationList
      conversations={conversations}
      activeId={activeConversationId}
      onSelect={select}
      onNew={startNewConversation}
      isLoading={conversationsQuery.isLoading}
    />
  );
}
